import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import {
  FiSearch,
  FiCalendar,
  FiClock,
  FiArrowRight,
  FiArrowUpRight,
  FiX,
  FiEdit3,
  FiTrash2,
  FiSend,
  FiBookOpen,
  FiMail,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
/* keep your real ./firebase.js — `db` is null in a preview build, and the page
   then falls back to demo + localStorage posts instead of crashing */
import { db, collection, addDoc, getDocs, deleteDoc, doc } from "./firebase";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "./Navbar";
import Foot from "./Foot";
import "./Hero.css";   // design system: tokens, buttons, grain, .kk-progress
import "./Blogs.css";  // everything unique to this page

/* ═══════════════ DATA ═══════════════ */

const ADMIN_EMAIL = "karanksxxx@gmail.com";
const CATS = ["All", "Tech", "Career", "Life", "Tutorials"];

const CAT_COLORS = {
  Tech: "#4ce0ff",
  Career: "#8b6bff",
  Life: "#c8ff3d",
  Tutorials: "#5eead4",
};

const FALLBACK_IMG = {
  Tech: "/img/proj-notes.jpg",
  Career: "/img/about-workspace.jpg",
  Life: "/img/proj-listing.jpg",
  Tutorials: "/img/proj-products.jpg",
};

const DEMO_BLOGS = [
  {
    id: "demo-1",
    title: "How I Started My Coding Journey",
    category: "Career",
    date: new Date("2026-05-23").toISOString(),
    image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=900&auto=format&fit=crop",
    content: `Everyone starts somewhere — my journey started in 2020 with a single HTML file and a lot of curiosity.\n\nI still remember the excitement of writing my first lines of code and watching them come alive in the browser. It felt like magic, and I was hooked instantly.\n\nSince then I've explored JavaScript, React, Node.js, Firebase and MongoDB. Each technology taught me something new — not just about code, but about problem solving and persistence.\n\nMy advice to beginners: build projects, break things, and never stop learning. Tutorials are great, but real growth happens when you build something of your own. Start small, stay consistent, and enjoy the process.`,
  },
  {
    id: "demo-2",
    title: "Best Practices for Clean Code",
    category: "Tech",
    date: new Date("2026-05-11").toISOString(),
    image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?q=80&w=900&auto=format&fit=crop",
    content: `Clean code is not about being clever — it's about being clear. Code is read far more often than it is written, so write for humans first.\n\nHere are the practices I follow in every project:\n\n1. Meaningful names — variables and functions should explain themselves.\n2. Small functions — each function should do one thing well.\n3. Consistent formatting — let tools like Prettier handle style.\n4. Comments for "why", not "what" — the code should show what it does.\n5. Delete dead code — don't comment it out, remove it. Git remembers.\n\nFollowing these habits has made my projects easier to maintain and a joy to revisit months later.`,
  },
  {
    id: "demo-3",
    title: "Why I Love React",
    category: "Tech",
    date: new Date("2026-05-05").toISOString(),
    image: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?q=80&w=900&auto=format&fit=crop",
    content: `React changed the way I think about building user interfaces. Components, hooks and the ecosystem make it possible to go from idea to product incredibly fast.\n\nWhat I love most is the component model — breaking a UI into small, reusable pieces just clicks with how I think. Hooks like useState and useEffect keep logic clean without classes.\n\nThe ecosystem is another superpower: React Router for navigation, Framer Motion for animation, Firebase for backend. Together they let a solo developer ship full products.\n\nIf you're learning web development in 2026, React is still one of the best investments you can make.`,
  },
  {
    id: "demo-4",
    title: "Building Real Projects",
    category: "Tutorials",
    date: new Date("2026-04-26").toISOString(),
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=900&auto=format&fit=crop",
    content: `Tutorial hell is real. You can watch 50 hours of videos and still freeze when starting a blank project. The cure? Build real things.\n\nHere's my process for every project:\n\n1. Pick a real problem — something you or someone you know actually needs.\n2. Start ugly — a working prototype beats a perfect plan.\n3. Ship early — deploy with Firebase or Vercel on day one.\n4. Iterate — improve one thing at a time based on feedback.\n\nMy projects RealtyCore and AushLink both started as rough ideas. Real projects teach you debugging, deployment, and design — skills no tutorial can fully give you.`,
  },
];

/* ═══════════════ HELPERS ═══════════════ */

const readTime = (content = "") => Math.max(1, Math.ceil(content.split(/\s+/).length / 200));

const formatShort = (ds) => {
  try {
    return new Date(ds).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return ds;
  }
};

const formatLong = (ds) => {
  try {
    return new Date(ds).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return ds;
  }
};

/* ═══════════════ ONE rAF LOOP — [data-plx] ═══════════════ */

function useParallax(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const items = Array.from(root.querySelectorAll("[data-plx]"));
    let raf = 0;

    const render = () => {
      const vh = window.innerHeight;
      for (const el of items) {
        const speed = parseFloat(el.dataset.plx) || 0;
        const r = el.getBoundingClientRect();
        if (r.bottom < -420 || r.top > vh + 420) continue;
        const y = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, [rootRef]);
}

/* ═══════════════ PARTICLES ═══════════════ */

function Particles() {
  const parts = useRef(
    Array.from({ length: 24 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      s: Math.random() * 3 + 1,
      d: Math.random() * 12 + 8,
      dl: Math.random() * 5,
    }))
  );
  return (
    <div className="bl-particles" aria-hidden="true">
      {parts.current.map((p) => (
        <motion.span
          key={p.id}
          className="bl-particle"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.s, height: p.s }}
          animate={{ y: [0, -24, 0], opacity: [0.08, 0.45, 0.08] }}
          transition={{ duration: p.d, repeat: Infinity, delay: p.dl, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

/* ═══════════════ EMPTY ═══════════════ */

function EmptyState() {
  return (
    <motion.div
      className="bl-empty"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="bl-empty__ic">
        <FiBookOpen />
      </div>
      <h3>No blogs yet</h3>
      <p>Be the first to share your thoughts and insights with the community.</p>
    </motion.div>
  );
}

/* ═══════════════ CARD ═══════════════ */

function BlogCard({ blog, index, onReadMore, onDelete, isAdmin }) {
  const cat = blog.category || "Tech";
  const open = () => onReadMore(blog);

  return (
    <motion.article
      className="bl-card"
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay: Math.min(index, 5) * 0.06, duration: 0.5 }}
    >
      <button className="bl-thumb" onClick={open} type="button" aria-label={blog.title}>
        <img
          src={blog.image || FALLBACK_IMG[cat]}
          alt={blog.title}
          loading="lazy"
          onError={(e) => {
            if (!e.currentTarget.dataset.fb) {
              e.currentTarget.dataset.fb = "1";
              e.currentTarget.src = FALLBACK_IMG[cat] || "/img/about-workspace.jpg";
            }
          }}
        />
        <span className="bl-cat" style={{ "--cat": CAT_COLORS[cat] || "#4ce0ff" }}>
          {cat}
        </span>
      </button>

      <div className="bl-info">
        <button className="bl-card__title" onClick={open} type="button">
          {blog.title}
        </button>
        <p className="bl-excerpt">
          {blog.content.length > 150 ? blog.content.substring(0, 150) + "…" : blog.content}
        </p>
        <div className="bl-meta">
          <span>
            <FiCalendar /> {formatShort(blog.date)}
          </span>
          <span>
            <FiClock /> {readTime(blog.content)} min read
          </span>
        </div>
      </div>

      <div className="bl-actions">
        <motion.button className="bl-read" onClick={open} whileHover={{ x: 4 }} whileTap={{ scale: 0.96 }}>
          Read more <FiArrowRight />
        </motion.button>
        {isAdmin && (
          <motion.button
            className="bl-del"
            title="Delete blog"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(blog.id);
            }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.9 }}
          >
            <FiTrash2 />
          </motion.button>
        )}
      </div>
    </motion.article>
  );
}

/* ═══════════════ MODAL ═══════════════ */

function BlogModal({ blog, onClose }) {
  if (!blog) return null;
  const words = blog.content.split(/\s+/).length;
  const cat = blog.category || "Tech";

  return (
    <motion.div
      className="bl-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bl-modal"
        initial={{ scale: 0.94, y: 30, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.94, y: 30, opacity: 0 }}
        transition={{ type: "spring", damping: 26, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        {blog.image && (
          <div className="bl-modal__img">
            <img
              src={blog.image}
              alt={blog.title}
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMG[cat] || "/img/about-workspace.jpg";
              }}
            />
          </div>
        )}

        <div className="bl-modal__head">
          <div>
            <span className="bl-cat" style={{ "--cat": CAT_COLORS[cat] || "#4ce0ff" }}>
              {cat}
            </span>
            <h2>{blog.title}</h2>
            <div className="bl-modal__meta">
              <span>
                <FiCalendar /> {formatLong(blog.date)}
              </span>
              <span>
                <FiClock /> {readTime(blog.content)} min read
              </span>
              <span>
                <FiBookOpen /> {words} words
              </span>
            </div>
          </div>
          <motion.button
            className="bl-modal__x"
            onClick={onClose}
            whileHover={{ rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Close"
          >
            <FiX />
          </motion.button>
        </div>

        <div className="bl-modal__body">
          {blog.content.split("\n").map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        <div className="bl-modal__foot">
          <button className="kk-btn kk-btn--ghost" onClick={onClose}>
            Close article
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ═══════════════ ADMIN FORM ═══════════════ */

function BlogForm({ title, content, category, image, setters, onSubmit }) {
  return (
    <motion.section
      className="bl-form-sec"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <div className="bl-form-head">
        <span className="kk-eyebrow">Admin — New post</span>
        <h2 className="kk-h2">
          Write a new <span className="kk-serif kk-acid">blog post</span>
        </h2>
        <p>Share your knowledge, insights and experiences with the community.</p>
      </div>

      <form onSubmit={onSubmit} className="bl-form">
        <div className="bl-form__row">
          <label className="bl-field" style={{ flex: 2 }}>
            <span>Title</span>
            <input
              type="text"
              placeholder="Enter an engaging title..."
              value={title}
              onChange={(e) => setters.setTitle(e.target.value)}
              required
            />
          </label>
          <label className="bl-field" style={{ flex: 1 }}>
            <span>Category</span>
            <select value={category} onChange={(e) => setters.setCategory(e.target.value)}>
              {CATS.slice(1).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="bl-field">
          <span>Cover image URL (optional)</span>
          <input
            type="url"
            placeholder="https://..."
            value={image}
            onChange={(e) => setters.setImage(e.target.value)}
          />
        </label>

        <label className="bl-field">
          <span>Content</span>
          <textarea
            placeholder="Write your content here... Share your thoughts, experiences, or knowledge."
            value={content}
            onChange={(e) => setters.setContent(e.target.value)}
            rows={8}
            required
          />
          <em className="bl-count">
            {content.length} characters · {readTime(content)} min read
          </em>
        </label>

        <motion.button
          type="submit"
          className="kk-btn"
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiSend /> Publish blog
        </motion.button>
      </form>
    </motion.section>
  );
}

/* ═══════════════ PAGE ═══════════════ */

export default function Blogs() {
  const rootRef = useRef(null);
  const [user, setUser] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("Tech");
  const [image, setImage] = useState("");
  const [blogs, setBlogs] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCat, setActiveCat] = useState("All");
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28 });

  const isAdmin = user && user.Email === ADMIN_EMAIL;

  useParallax(rootRef);

  useEffect(() => {
    try {
      const loggedInUser = JSON.parse(localStorage.getItem("user"));
      if (loggedInUser) setUser(loggedInUser);
    } catch {
      /* ignore */
    }
    fetchBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBlogs = async () => {
    /* preview / unconfigured Firebase: demo posts + anything saved locally */
    if (!db) {
      try {
        const local = JSON.parse(localStorage.getItem("demo-blogs") || "[]");
        setBlogs([...local, ...DEMO_BLOGS]);
      } catch {
        setBlogs(DEMO_BLOGS);
      }
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const snap = await getDocs(collection(db, "Blogs"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setBlogs(list.reverse());
    } catch (error) {
      console.error("Error fetching blogs:", error);
      toast.error("Failed to load blogs");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      toast.error("Title and content cannot be empty!");
      return;
    }
    const newBlog = {
      title: title.trim(),
      content: content.trim(),
      category,
      image: image.trim() || "",
      date: new Date().toISOString(),
    };

    if (!db) {
      const withId = { ...newBlog, id: "local-" + Date.now() };
      try {
        const local = JSON.parse(localStorage.getItem("demo-blogs") || "[]");
        localStorage.setItem("demo-blogs", JSON.stringify([withId, ...local]));
      } catch {
        /* ignore */
      }
      setBlogs((p) => [withId, ...p]);
      toast.success("Blog published successfully! 🎉");
      setTitle("");
      setContent("");
      setImage("");
      setCategory("Tech");
      return;
    }

    try {
      await addDoc(collection(db, "Blogs"), newBlog);
      toast.success("Blog published successfully! 🎉");
      setTitle("");
      setContent("");
      setImage("");
      setCategory("Tech");
      fetchBlogs();
    } catch (error) {
      console.error("Error posting blog:", error);
      toast.error("Failed to publish blog.");
    }
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;

    if (!db || String(blogId).startsWith("demo-") || String(blogId).startsWith("local-")) {
      try {
        const local = JSON.parse(localStorage.getItem("demo-blogs") || "[]");
        localStorage.setItem("demo-blogs", JSON.stringify(local.filter((b) => b.id !== blogId)));
      } catch {
        /* ignore */
      }
      setBlogs((p) => p.filter((b) => b.id !== blogId));
      toast.success("Blog deleted successfully!");
      return;
    }

    try {
      await deleteDoc(doc(db, "Blogs", blogId));
      toast.success("Blog deleted successfully!");
      fetchBlogs();
    } catch (error) {
      console.error("Error deleting blog:", error);
      toast.error("Failed to delete blog.");
    }
  };

  const filtered = blogs.filter((b) => {
    const matchCat = activeCat === "All" || (b.category || "Tech") === activeCat;
    const q = searchTerm.trim().toLowerCase();
    const matchQ =
      !q || b.title.toLowerCase().includes(q) || b.content.toLowerCase().includes(q);
    return matchCat && matchQ;
  });

  return (
    <div className="kk-root bl-page" ref={rootRef}>
      <motion.div className="kk-progress" style={{ scaleX: progress }} />
      <Particles />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar
        theme="dark"
        toastStyle={{
          background: "#0d0d14",
          border: "1px solid rgba(200,255,61,.25)",
          color: "#f3f3f5",
          borderRadius: "14px",
        }}
      />

   

      {/* ══════════════ HERO ══════════════ */}
      <header className="bl-hero">
        <div className="bl-hero__bg" aria-hidden="true">
          <span className="bl-hero__grid" data-plx="0.05" />
          <span className="bl-hero__glow bl-hero__glow--acid" data-plx="0.16" />
          <span className="bl-hero__glow bl-hero__glow--violet" data-plx="0.24" />
          <span className="bl-hero__word" data-plx="0.1">
            JOURNAL
          </span>
        </div>

        <div className="kk-wrap bl-hero__in">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bl-kicker">
              <span className="kk-dot" />
              <span>Blog</span>
              <span className="kk-slash">/</span>
              <span>Insights</span>
            </div>

            <h1 className="bl-title">
              <motion.span
                className="bl-title__line"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              >
                My <span className="kk-serif kk-acid">Blogs</span>
              </motion.span>
              <motion.span
                className="bl-title__sub"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                thoughts, ideas and learnings.
              </motion.span>
            </h1>

            <p className="bl-lead">
              Exploring technology, sharing what I learn, and documenting the journey of a
              developer — one post at a time.
            </p>
          </motion.div>
        </div>
      </header>

      {/* ══════════════ TOOLBAR ══════════════ */}
      <div className="bl-bar">
        <div className="kk-wrap bl-bar__in">
          <div className="bl-tabs">
            {CATS.map((c) => (
              <button
                key={c}
                className={`bl-tab ${activeCat === c ? "active" : ""}`}
                onClick={() => setActiveCat(c)}
                type="button"
              >
                {c}
                {c === "All" && <em>{blogs.length}</em>}
              </button>
            ))}
          </div>

          <div className="bl-search">
            <FiSearch className="bl-search__ic" />
            <input
              type="text"
              placeholder="Search blogs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <motion.button
                className="bl-search__x"
                onClick={() => setSearchTerm("")}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                type="button"
                aria-label="Clear search"
              >
                <FiX />
              </motion.button>
            )}
          </div>
        </div>
      </div>

      {/* ══════════════ LIST ══════════════ */}
      <main className="kk-section kk-tight bl-main">
        <div className="kk-wrap">
          {loading ? (
            <div className="bl-loading">
              <div className="bl-spinner" />
              <p>Loading blogs…</p>
            </div>
          ) : filtered.length === 0 ? (
            searchTerm || activeCat !== "All" ? (
              <motion.div className="bl-nores" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <p>
                  No blogs found
                  {searchTerm && (
                    <>
                      {" "}
                      matching &ldquo;<strong>{searchTerm}</strong>&rdquo;
                    </>
                  )}
                  .
                </p>
                <button
                  className="kk-btn kk-btn--ghost"
                  onClick={() => {
                    setSearchTerm("");
                    setActiveCat("All");
                  }}
                >
                  Clear filters
                </button>
              </motion.div>
            ) : (
              <EmptyState />
            )
          ) : (
            <div className="bl-list">
              {filtered.map((blog, i) => (
                <BlogCard
                  key={blog.id}
                  blog={blog}
                  index={i}
                  onReadMore={setSelectedBlog}
                  onDelete={handleDelete}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          )}

          {isAdmin && (
            <BlogForm
              title={title}
              content={content}
              category={category}
              image={image}
              setters={{ setTitle, setContent, setCategory, setImage }}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      </main>

      {/* ══════════════ CTA ══════════════ */}
      <section className="bl-cta">
        <div className="bl-cta__orbs" aria-hidden="true">
          <span data-plx="0.1" />
          <span data-plx="0.18" />
        </div>
        <div className="kk-wrap bl-cta__in">
          <span className="kk-eyebrow">Keep in touch</span>
          <h2 className="kk-h2">
            Got a topic you want{" "}
            <span className="kk-serif kk-acid">written about?</span>
          </h2>
          <p>Send it over — the best questions usually become the next post.</p>
          <div className="bl-cta__btns">
            <a className="kk-btn kk-btn--lg" href="mailto:karanksxxx@gmail.com?subject=Blog%20idea">
              <FiMail /> Suggest a topic
            </a>
            <a className="kk-btn kk-btn--ghost kk-btn--lg" href="/projects">
              See the work <FiArrowUpRight />
            </a>
          </div>
        </div>
      </section>
   <Navbar />
      <Foot />

      <a
        className="kk-wa"
        href="https://wa.me/919041429065"
        target="_blank"
        rel="noreferrer"
        title="Chat on WhatsApp"
      >
        <FaWhatsapp />
      </a>

      <AnimatePresence>
        {selectedBlog && <BlogModal blog={selectedBlog} onClose={() => setSelectedBlog(null)} />}
      </AnimatePresence>
    </div>
  );
}